import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  deleteUser,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';

// Firebase 설정
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

// Firebase 앱 초기화
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const functions = getFunctions(app, 'asia-northeast3');

// 간소화된 에러 메시지
const errorMessages = {
  'auth/user-not-found': '사용자를 찾을 수 없습니다.',
  'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
  'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
  'auth/weak-password': '비밀번호가 너무 약합니다.',
  'auth/invalid-email': '유효하지 않은 이메일입니다.',
  'auth/too-many-requests': '너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.',
  'auth/network-request-failed': '네트워크 오류가 발생했습니다.',
  'auth/popup-closed-by-user': '로그인 창이 닫혔습니다.',
  'auth/cancelled-popup-request': '로그인이 취소되었습니다.',
  'auth/popup-blocked': '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.',
  'auth/account-exists-with-different-credential': '다른 방법으로 가입된 계정입니다.',
  'auth/operation-not-allowed': '이 작업이 허용되지 않습니다.',
  'auth/user-disabled': '비활성화된 계정입니다.',
  'auth/invalid-credential': '유효하지 않은 인증 정보입니다.',
  'auth/requires-recent-login': '보안을 위해 다시 로그인해주세요.',
  'auth/quota-exceeded': '할당량을 초과했습니다.',
  'auth/unavailable': '서비스가 일시적으로 사용할 수 없습니다.',
  'auth/insufficient-permission': '권한이 부족합니다.',
  'auth/invalid-argument': '잘못된 인수가 전달되었습니다.',
  'auth/not-found': '요청한 리소스를 찾을 수 없습니다.',
  'auth/already-exists': '이미 존재하는 리소스입니다.',
  'auth/resource-exhausted': '리소스가 소진되었습니다.',
  'auth/failed-precondition': '사전 조건이 충족되지 않았습니다.',
  'auth/aborted': '작업이 중단되었습니다.',
  'auth/out-of-range': '값이 범위를 벗어났습니다.',
  'auth/unimplemented': '구현되지 않은 기능입니다.',
  'auth/internal': '내부 오류가 발생했습니다.',
  'auth/data-loss': '데이터 손실이 발생했습니다.',
  'auth/unknown': '알 수 없는 오류가 발생했습니다.'
};

class AuthService {
  constructor() {
    this.auth = auth;
    this.functions = functions;
    this.googleProvider = new GoogleAuthProvider();
  }

  // 계정 생성
  async createAccount(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 로그인
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      
      // 마지막 로그인 시간을 Realtime Database에 저장
      try {
        const realtimeService = (await import('./realtimeService')).default;
        realtimeService.setUserOnline(userCredential.user.uid);
      } catch (error) {
        console.warn('Failed to update last login time:', error);
      }
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // Google 로그인
  async googleLogin() {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      
      // 마지막 로그인 시간을 Realtime Database에 저장
      try {
        const realtimeService = (await import('./realtimeService')).default;
        realtimeService.setUserOnline(result.user.uid);
      } catch (error) {
        console.warn('Failed to update last login time:', error);
      }
      
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 로그아웃
  async logout() {
    try {
      const currentUser = this.auth.currentUser;
      const userId = currentUser ? currentUser.uid : null;
      
      await signOut(this.auth);
      
      // 명시적으로 오프라인 상태로 설정
      if (userId) {
        try {
          const realtimeService = (await import('./realtimeService')).default;
          realtimeService.setUserOffline(userId);
        } catch (error) {
          console.warn('Failed to set user offline status:', error);
        }
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 비밀번호 재설정 이메일 전송
  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(this.auth, email);
      return { success: true };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 비밀번호 재설정 확인
  async confirmPasswordReset(oobCode, newPassword) {
    try {
      await confirmPasswordReset(this.auth, oobCode, newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 사용자 삭제 (Cloud Function)
  async deleteUser(uid) {
    try {
      const deleteUserFunction = httpsCallable(this.functions, 'deleteUserByUid');
      await deleteUserFunction({ uid });
      return { success: true };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 현재 사용자 삭제 (Firebase Auth)
  async deleteCurrentUser() {
    try {
      const user = this.auth.currentUser;
      if (user) {
        await deleteUser(user);
        return { success: true };
      }
      return { success: false, error: '로그인된 사용자가 없습니다.' };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 인증 상태 변경 리스너
  onAuthStateChanged(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  // 현재 사용자 가져오기
  getCurrentUser() {
    return this.auth.currentUser;
  }

  // 토큰 새로고침
  async refreshUserToken() {
    try {
      const user = this.auth.currentUser;
      if (user) {
        const token = await user.getIdToken(true);
        return { success: true, token };
      }
      return { success: false, error: '로그인된 사용자가 없습니다.' };
    } catch (error) {
      return { success: false, error: this.getErrorMessage(error.code) };
    }
  }

  // 에러 메시지 가져오기
  getErrorMessage(errorCode) {
    return errorMessages[errorCode] || '알 수 없는 오류가 발생했습니다.';
  }
}

// 싱글톤 인스턴스 생성
const authService = new AuthService();

export default authService; 